import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from 'reactstrap';
import { Link, useLocation, useNavigate } from 'react-router-dom';

// Helper function to handle hash navigation
const useHashNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleHashClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // Check if it's a hash link (starts with /# or just #)
    if (href.startsWith('/#') || href.startsWith('#')) {
      const hash = href.startsWith('/#') ? href.substring(2) : href.substring(1);
      const isOnLandingPage = location.pathname === '/' || location.pathname === '/landing';

      if (isOnLandingPage) {
        // We're on the landing page, just scroll
        e.preventDefault();
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      } else {
        // We're on a different page, navigate to landing with hash
        e.preventDefault();
        navigate('/#' + hash);
      }
    }
    // For non-hash links, let the default behavior happen
  };

  return handleHashClick;
};

// Dropdown Menu Component
const DropdownMenu: React.FC<{
  title: string;
  items: { name: string; href: string; icon?: string; description?: string; badge?: string }[];
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}> = ({ title, items, isOpen, onToggle, onClose }) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const handleHashClick = useHashNavigation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  const handleItemClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    handleHashClick(e, href);
    onClose();
  };

  return (
    <div className="nav-dropdown" ref={dropdownRef}>
      <button className="nav-link nav-dropdown-btn" onClick={onToggle}>
        {title}
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginLeft: '4px', transition: 'transform 0.2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0)' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {/* SEO: always render the dropdown into the DOM so the prerendered HTML
          carries these <a href> links for Googlebot. Visibility is CSS-only
          (display), not conditional rendering. AnimatePresence removed because
          there is no unmount to animate any more. */}
      <motion.div
        className="dropdown-menu-custom"
        style={{ display: isOpen ? 'block' : 'none' }}
        initial={false}
        animate={isOpen ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: 10, scale: 0.95 }}
        transition={{ duration: 0.2 }}
      >
        {items.map((item, idx) => (
          <a key={idx} href={item.href} className="dropdown-item-custom" onClick={(e) => handleItemClick(e, item.href)}>
            {item.icon && <span className="dropdown-icon">{item.icon}</span>}
            <div className="dropdown-item-content">
              <span className="dropdown-item-name">
                {item.name}
                {item.badge && <span className="dropdown-badge">{item.badge}</span>}
              </span>
              {item.description && <span className="dropdown-item-desc">{item.description}</span>}
            </div>
          </a>
        ))}
      </motion.div>
    </div>
  );
};

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const handleHashClick = useHashNavigation();
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const toggle = () => setIsOpen(!isOpen);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY >= 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const featuresItems = [
    { name: 'AI-Powered Features', href: '/#ai-features', icon: '🤖', description: 'Supercharge your workflow with AI' },
    { name: 'Smart Repricing', href: '/#features', icon: '💰', description: 'Auto-adjust prices for max profit' },
    { name: 'Stock Sync', href: '/#features', icon: '📦', description: '24/7 inventory monitoring' },
    { name: 'Order Management', href: '/#features', icon: '📋', description: 'One-click order processing', badge: 'Soon' },
    { name: 'eBay Templates', href: '/#features', icon: '🎨', description: '9 professional listing designs' },
    { name: 'Powerful Dashboard', href: '/#dashboard', icon: '📊', description: 'Simple interface, complete control' },
  ];

  const integrationsItems = [
    { name: 'eBay', href: '/#integrations', icon: '🛒', description: 'Full API integration', badge: 'Active' },
    { name: 'Amazon', href: '/#integrations', icon: '📦', description: 'Source products from Amazon UK/US/DE' },
    { name: 'Shopify', href: '/#integrations', icon: '🏪', description: 'Multi-channel selling', badge: 'Coming Soon' },
    { name: 'Walmart', href: '/#integrations', icon: '🏬', description: 'Expand to US market', badge: 'Coming Soon' },
    { name: 'TikTok Shop', href: '/#integrations', icon: '🎵', description: 'Social commerce', badge: 'Coming Soon' },
    { name: 'Etsy', href: '/#integrations', icon: '🎁', description: 'Handmade & vintage', badge: 'Coming Soon' },
  ];

  const resourcesItems = [
    { name: 'Help Center', href: '/help', icon: '📚', description: 'Guides & tutorials' },
    { name: 'Blog', href: '/blog', icon: '✍️', description: 'Tips & industry news' },
    { name: 'FAQ', href: '/faq', icon: '❓', description: 'Common questions answered' },
    { name: 'Community', href: '/community', icon: '👥', description: 'Join our Discord' },
    { name: 'Compare Tools', href: '/compare/coderom-vs-autods', icon: '⚖️', description: 'See how Coderom compares' },
      ];

  const toolsItems = [
    { name: 'eBay Fee Calculator', href: '/tools/ebay-fee-calculator', icon: '🧮', description: 'Calculate eBay fees for UK, US, DE, AU' },
    { name: 'Profit Calculator', href: '/tools/profit-calculator', icon: '💹', description: 'Amazon→eBay dropshipping profit' },
    { name: 'eBay Title Optimizer', href: '/tools/title-optimizer', icon: '✨', description: 'AI-powered title optimization' },
  ];

  const simpleLinks = [
    { name: 'How It Works', href: '/#how-it-works' },
    { name: 'Pricing', href: '/pages-pricing' },
  ];

  return (
    <motion.nav
      className={`landing-navbar ${isScrolled ? 'scrolled' : ''}`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] as const }}
    >
      <Container>
        <div className="navbar-inner">
          {/* Logo */}
          <Link to="/" className="navbar-brand" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
            <img
              src="/logo-full.png"
              alt="CodeRom"
              style={{ height: '40px', width: 'auto' }}
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="navbar-links-desktop">
            {/* Features Dropdown */}
            <DropdownMenu
              title="Features"
              items={featuresItems}
              isOpen={activeDropdown === 'features'}
              onToggle={() => setActiveDropdown(activeDropdown === 'features' ? null : 'features')}
              onClose={() => setActiveDropdown(null)}
            />

            {/* Integrations Dropdown */}
            <DropdownMenu
              title="Integrations"
              items={integrationsItems}
              isOpen={activeDropdown === 'integrations'}
              onToggle={() => setActiveDropdown(activeDropdown === 'integrations' ? null : 'integrations')}
              onClose={() => setActiveDropdown(null)}
            />

            {/* Simple Links */}
            {simpleLinks.map((link, index) => (
              <a key={index} href={link.href} className="nav-link" onClick={(e) => handleHashClick(e, link.href)}>
                {link.name}
              </a>
            ))}

            {/* Resources Dropdown */}
            <DropdownMenu
              title="Resources"
              items={resourcesItems}
              isOpen={activeDropdown === 'resources'}
              onToggle={() => setActiveDropdown(activeDropdown === 'resources' ? null : 'resources')}
              onClose={() => setActiveDropdown(null)}
            />

            {/* Tools Dropdown */}
            <DropdownMenu
              title="Tools"
              items={toolsItems}
              isOpen={activeDropdown === 'tools'}
              onToggle={() => setActiveDropdown(activeDropdown === 'tools' ? null : 'tools')}
              onClose={() => setActiveDropdown(null)}
            />
          </div>

          {/* Desktop CTA */}
          <div className="navbar-cta-desktop">
            <a href={`${process.env.REACT_APP_APP_URL || ''}/login`} className="nav-link-signin">
              Sign In
            </a>
            <a href={`${process.env.REACT_APP_APP_URL || ''}/register`} className="nav-btn-primary">
              <span>Start Free</span>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="5" y1="12" x2="19" y2="12" />
                <polyline points="12 5 19 12 12 19" />
              </svg>
            </a>
          </div>

          {/* Mobile Toggle */}
          <button
            className={`navbar-toggle ${isOpen ? 'active' : ''}`}
            onClick={toggle}
            aria-label="Toggle navigation"
          >
            <span className="toggle-line" />
            <span className="toggle-line" />
            <span className="toggle-line" />
          </button>
        </div>

      </Container>

      {/* Mobile Menu - Using Portal to render directly to body */}
      {typeof document !== 'undefined' && createPortal(
        <AnimatePresence>
          {isOpen && (
            <>
              {/* Backdrop */}
              <motion.div
                style={{
                  position: 'fixed',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  zIndex: 99998,
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setIsOpen(false)}
              />
              {/* Menu Panel */}
              <motion.div
                style={{
                  position: 'fixed',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: '100%',
                  maxWidth: '320px',
                  backgroundColor: '#ffffff',
                  zIndex: 99999,
                  display: 'flex',
                  flexDirection: 'column',
                  padding: '24px',
                  boxShadow: '-4px 0 24px rgba(0, 0, 0, 0.15)',
                  overflowY: 'auto',
                }}
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'tween', duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
              >
                {/* Close Button */}
                <button
                  style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    width: '44px',
                    height: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#f3f4f6',
                    border: 'none',
                    borderRadius: '12px',
                    color: '#374151',
                    cursor: 'pointer',
                  }}
                  onClick={() => setIsOpen(false)}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>

                {/* Navigation Links */}
                <nav style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '80px', flex: 1 }}>
                  {[
                    { name: 'Features', href: '/#features' },
                    { name: 'Integrations', href: '/#integrations' },
                    { name: 'How It Works', href: '/#how-it-works' },
                    { name: 'Pricing', href: '/pages-pricing' },
                    { name: 'FAQ', href: '/#faq' },
                    { name: 'eBay Fee Calculator', href: '/tools/ebay-fee-calculator' },
                    { name: 'Profit Calculator', href: '/tools/profit-calculator' },
                    { name: 'eBay Title Optimizer', href: '/tools/title-optimizer' },
                    { name: 'Compare Tools', href: '/compare/coderom-vs-autods' },
                  ].map((link, idx) => (
                    <motion.a
                      key={link.name}
                      href={link.href}
                      style={{
                        display: 'block',
                        padding: '16px 20px',
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#1f2937',
                        textDecoration: 'none',
                        borderRadius: '12px',
                        background: 'transparent',
                      }}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                      onClick={(e) => { handleHashClick(e, link.href); setIsOpen(false); }}
                    >
                      {link.name}
                    </motion.a>
                  ))}
                </nav>

                {/* CTA Buttons */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                    paddingTop: '24px',
                    borderTop: '1px solid #e5e7eb',
                    marginTop: 'auto',
                  }}
                >
                  <a
                    href={`${process.env.REACT_APP_APP_URL || ''}/login`}
                    style={{
                      display: 'block',
                      padding: '14px 20px',
                      textAlign: 'center',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#374151',
                      textDecoration: 'none',
                      border: '1px solid #e5e7eb',
                      borderRadius: '12px',
                      background: '#ffffff',
                    }}
                    onClick={() => setIsOpen(false)}
                  >
                    Sign In
                  </a>
                  <a
                    href={`${process.env.REACT_APP_APP_URL || ''}/register`}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      padding: '14px 20px',
                      fontSize: '16px',
                      fontWeight: 600,
                      color: 'white',
                      textDecoration: 'none',
                      background: 'linear-gradient(135deg, #f97316, #fb923c)',
                      borderRadius: '12px',
                      boxShadow: '0 4px 14px rgba(249, 115, 22, 0.3)',
                    }}
                    onClick={() => setIsOpen(false)}
                  >
                    Start Free Trial
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="5" y1="12" x2="19" y2="12" />
                      <polyline points="12 5 19 12 12 19" />
                    </svg>
                  </a>
                </div>

                {/* Footer Info */}
                <div style={{ textAlign: 'center', paddingTop: '20px', marginTop: '16px' }}>
                  <p style={{ fontSize: '14px', fontWeight: 600, color: '#374151', margin: '0 0 4px 0' }}>
                    AI-Powered Dropshipping Platform
                  </p>
                  <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                    14-day free trial • No credit card required
                  </span>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>,
        document.body
      )}

      {/* Dropdown Styles */}
      <style>{`
        .nav-dropdown {
          position: relative;
        }
        .nav-dropdown-btn {
          display: flex;
          align-items: center;
          background: none;
          border: none;
          cursor: pointer;
          font-size: inherit;
          color: inherit;
          padding: 8px 12px;
          white-space: nowrap;
        }
        .dropdown-menu-custom {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background: white;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
          padding: 12px;
          min-width: 300px;
          z-index: 1000;
        }
        .dropdown-item-custom {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 12px 14px;
          border-radius: 10px;
          text-decoration: none;
          color: #1f2937;
          transition: all 0.2s;
        }
        .dropdown-item-custom:hover {
          background: linear-gradient(135deg, #fff7ed, #ffedd5);
          transform: translateX(4px);
        }
        .dropdown-icon {
          font-size: 20px;
          width: 32px;
          height: 32px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border-radius: 8px;
          flex-shrink: 0;
        }
        .dropdown-item-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }
        .dropdown-item-name {
          font-weight: 600;
          font-size: 14px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .dropdown-item-desc {
          font-size: 12px;
          color: #6b7280;
        }
        .dropdown-badge {
          font-size: 10px;
          padding: 2px 8px;
          border-radius: 20px;
          font-weight: 600;
          text-transform: uppercase;
        }
        .dropdown-badge:contains('Active') {
          background: #dcfce7;
          color: #16a34a;
        }
        .dropdown-item-custom:has(.dropdown-badge) .dropdown-badge {
          background: #fef3c7;
          color: #d97706;
        }
        .dropdown-item-custom .dropdown-badge:first-of-type {
          background: #dcfce7;
          color: #16a34a;
        }

        /* Mobile Menu - Clean Overlay Style */
        .mobile-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 998;
        }

        .mobile-menu-panel {
          position: fixed;
          top: 0;
          right: 0;
          bottom: 0;
          width: 100%;
          max-width: 320px;
          background: white;
          z-index: 999;
          display: flex;
          flex-direction: column;
          padding: 24px;
          box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
        }

        .mobile-close-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 44px;
          height: 44px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
          border: none;
          border-radius: 12px;
          color: #374151;
          cursor: pointer;
          transition: all 0.2s;
        }

        .mobile-close-btn:hover {
          background: #e5e7eb;
          color: #111827;
        }

        .mobile-nav {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 80px;
          flex: 1;
        }

        .mobile-nav-link {
          display: block;
          padding: 16px 20px;
          font-size: 18px;
          font-weight: 600;
          color: #1f2937;
          text-decoration: none;
          border-radius: 12px;
          transition: all 0.2s;
        }

        .mobile-nav-link:hover {
          background: #f9fafb;
          color: #f97316;
        }

        .mobile-cta-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding-top: 24px;
          border-top: 1px solid #e5e7eb;
          margin-top: auto;
        }

        .mobile-cta-signin {
          display: block;
          padding: 14px 20px;
          text-align: center;
          font-size: 16px;
          font-weight: 600;
          color: #374151;
          text-decoration: none;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          transition: all 0.2s;
        }

        .mobile-cta-signin:hover {
          background: #f9fafb;
          border-color: #d1d5db;
        }

        .mobile-cta-primary {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 20px;
          font-size: 16px;
          font-weight: 600;
          color: white;
          text-decoration: none;
          background: linear-gradient(135deg, #f97316, #fb923c);
          border-radius: 12px;
          transition: all 0.2s;
          box-shadow: 0 4px 14px rgba(249, 115, 22, 0.3);
        }

        .mobile-cta-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(249, 115, 22, 0.4);
        }

        .mobile-footer-info {
          text-align: center;
          padding-top: 20px;
          margin-top: 16px;
        }

        .mobile-footer-info p {
          font-size: 14px;
          font-weight: 600;
          color: #374151;
          margin: 0 0 4px 0;
        }

        .mobile-footer-info span {
          font-size: 12px;
          color: #9ca3af;
        }

        /* Hide mobile menu on desktop */
        @media (min-width: 992px) {
          .mobile-backdrop,
          .mobile-menu-panel {
            display: none !important;
          }
        }
      `}</style>
    </motion.nav>
  );
};

export default Navbar;
